import {Box, Button, styled, Stack, useTheme, Container, BoxProps} from "@mui/material";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import {ChangeEvent, useEffect, useState} from "react";
import Typography from "@mui/material/Typography";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import {Delete} from "@mui/icons-material";
import {IDropzoneFile} from "@buildwitt/shapeshift-common/v1/fileDropzone/fileDropzoneFile";

interface EmptyLogoContentProps {
	label?: string;
	emptyLabelSuggestion?: string;
	formatsSupported?: string;
}
const EmptyLogoContent = ({
	label,
	emptyLabelSuggestion, formatsSupported
}: EmptyLogoContentProps): JSX.Element => {
	const theme = useTheme();
	return (
		<Stack spacing={1} data-testid="emptyLogoContentStack">
			<Container
				sx={{width: "100%", textAlign: "center"}}
				data-testid="emptyLogoContentContainer"
			>
				<UploadFileOutlinedIcon
					sx={{color: theme.palette.primary.main}}
					data-testid="emptyLogoContentIcon"
				/>
			</Container>
			<Typography
				typography="subtitle1"
				sx={{
					color: theme.palette.text.primary,
					textAlign: "center"
				}}
				data-testid="emptyLogoContentPrimaryText"
			>
				{label}
			</Typography>
			<Typography
				typography="caption"
				sx={{
					color: theme.palette.secondary.dark,
					textAlign: "center"
				}}
				data-testid="emptyLogoContentSecondaryText"
			>
				{formatsSupported}
			</Typography>
			<Typography
				typography="caption"
				sx={{
					color: theme.palette.secondary.dark,
					textAlign: "center"
				}}
				data-testid="emptyLogoContentRecommendationText"
			>
				{emptyLabelSuggestion}
			</Typography>
		</Stack>
	);
};

export interface ImageSelectorProps {
	onImageSelected?: (files?: IDropzoneFile[]) => void;
	label?: string;
	emptyLabel?: string;
	defaultImageUrl?: string;
	showDeleteIcon?: boolean;
	emptyLabelSuggestion?: string;
	imageValidTypes?: string[];
	formatsSupported?: string;
	disabled?: boolean;
	error?: string;
}

const ImageSelector = ({
	onImageSelected,
	defaultImageUrl,
	label,
	emptyLabel,
	showDeleteIcon,
	emptyLabelSuggestion,
	imageValidTypes = [
		"image/png",
		"image/jpeg",
		"image/gif",
		"image/svg+xml"
	],
	formatsSupported = "SVG, PNG, JPG or GIF (max. 3MB)",
	disabled,
	error
}: ImageSelectorProps): JSX.Element => {
	const [uploadedPreview, setUploadedPreview] = useState<string | undefined>();
	const image = uploadedPreview !== "_deleted_" ? uploadedPreview || defaultImageUrl : undefined;

	const handleImageChange = (event: ChangeEvent<HTMLInputElement>): void => {
		const file = event.target.files?.[0];

		if (!file) {
			setUploadedPreview(undefined);
			onImageSelected?.();
			return
		}

		const fileType = file.type;
		setUploadedPreview(URL.createObjectURL(file));
		onImageSelected?.([{file, name: file.name, type: fileType, preview: URL.createObjectURL(file)}]);
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
		if (event.key === "Enter") {
			document.getElementById("uploadProfilePicture")?.click();
			event.preventDefault();
		}
	};

	useEffect(() => {
		if (disabled && uploadedPreview) {
			setUploadedPreview(undefined);
		}
	}, [disabled]);

	return (
		<Box sx={{position: "relative"}} data-testid="logoSelectorBox">
			<CompanyLogoDiv
				$disabled={disabled}
				component="fieldset"
				data-testid="logoSelectorCompanyLogoDiv"
				onClick={() => {
					const elem = document.getElementById("uploadProfilePicture")
					if (elem) {
						elem.click();
					}
				}}
				sx={{cursor: "pointer"}}
			>
				<legend>
					<LabelOutline data-testid="logoSelectorTitleLabel">
						{label}
					</LabelOutline>
				</legend>
				{image ? (
					<img
						src={image}
						style={{width: "100%", height: "100%", objectFit: "contain"}}
						alt={label}
						data-testid="logoSelectorImage"
					/>
				) : (
					<EmptyLogoContent
						label={emptyLabel}
						emptyLabelSuggestion={emptyLabelSuggestion}
						formatsSupported={formatsSupported}
					/>
				)}
			</CompanyLogoDiv>
			{showDeleteIcon && (
				<Button
					component="span"
					id="deleteButton"
					variant="outlined"
					sx={{
						minWidth: "45px",
						position: "absolute",
						marginLeft: 25,
						bottom: -10,
						backgroundColor: "#302120",
						borderColor: "error.main",
						"&:hover": {
							backgroundColor: "#4C2724",
							borderColor: "error.main"
						}
					}}
					color="error"
					data-testid="logoSelectorDeleteButton"
					onClick={() => {
						setUploadedPreview("_deleted_");
						onImageSelected?.(undefined);
					}}
				>
					<Delete fontSize="small" />
				</Button>
			)}
			<input
				id="uploadProfilePicture"
				accept={imageValidTypes.join(",")}
				type="file"
				hidden
				disabled={disabled}
				onChange={handleImageChange}
				data-testid="logoSelectorFileInput"
			/>
			<label
				htmlFor="uploadProfilePicture"
				data-testid="logoSelectorFileUploadLabel"
			>
				<Button
					disabled={disabled}
					component="span"
					id="uploadButton"
					variant="contained"
					sx={{
						minWidth: "52px",
						position: "absolute",
						marginLeft: 33,
						bottom: -10,
						"&.Mui-disabled": {
							backgroundColor: "#302120"
						}
					}}
					data-testid="logoSelectorFileUploadButton"
					onKeyDown={handleKeyDown}
				>
					<AddPhotoAlternateOutlinedIcon fontSize="small" />
				</Button>
			</label>
			{error && <Typography variant="caption" color="error" sx={{position: "absolute"}}>{error}</Typography>}
		</Box>
	);
};

const CompanyLogoDiv = styled(Box)<BoxProps & {$disabled?: boolean}>(({theme, $disabled}) => ({
	borderColor: "#ffffff3b",
	width: "270px",
	height: "190px",
	borderWidth: 1,
	borderStyle: "solid",
	borderRadius: "4px",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	"&:hover": {
		borderColor: $disabled ? undefined : theme.palette.secondary.light
	}
}));

const LabelOutline = styled(Typography)(({theme}) => {
	return ({
		fontSize: theme.typography.caption.fontSize,
	})
});

export default ImageSelector;
